import type { ReferenceLayerIds } from './aws-map/aws-map-style.utils'
import {
  CITY_LEVEL_ZOOM,
  COMPLETE_ADDRESS_MIN_ZOOM,
  NO_REFERENCE_FALLBACK_ZOOM,
  PLACE_TYPE_TARGET_ZOOM,
} from './location.constants'
import type {
  MapReference,
  ReferenceSignal,
  ReferenceSignals,
  ReferenceStrategyId,
  ViewPoint,
  ViewProbe,
  ViewResolution,
} from './map-landing.types'

// DRRWEB-3662 — the landing model moved next to the landing helpers. These re-exports keep the
// existing importers (and their tests) compiling; new code imports from the model files directly.
// (`toLocationKey` lives in map-landing.utils, which imports from here — no value re-export, to
// keep the module graph acyclic.)
export type {
  MapLanding,
  MapReference,
  NearbyLandmark,
  NearbyLandmarkStrength,
  ReferenceSignal,
  ReferenceSignals,
  ReferenceStrategyId,
  ViewPoint,
  ViewProbe,
  ViewResolution
} from './map-landing.types'

/**
 * True when a signal carries candidates whose relevance depends on the current view. A store-side
 * consumer must treat such a signal as *not yet reported* — only the map component, which knows
 * the canvas and its padding, can turn it into a measured 'poi' / 'weak' / 'none'.
 */
export const isViewDependent = (signal: ReferenceSignal | null | undefined): boolean =>
  Boolean(signal?.landmarks?.length)

/**
 * Ordered first-decisive-wins arbitration across an arm's strategy chain:
 *  - the first signal with reference !== 'none' wins; later strategies in the chain are ignored
 *  - if every strategy that answered reported 'none', the arbitration is 'none' (widen)
 *  - a strategy that answered `null` ("no opinion") is skipped, so the rest of the chain still
 *    decides — a detector that cannot answer must never silence one that can
 *  - a strategy that has not reported at all (absent) returns null (no decision), because its
 *    answer may still arrive; nothing is widened on an incomplete chain
 *  - if no strategy in the chain answered, the result is null — control behaviour
 *  - when the verdict is 'none', the 'none' signal that knows *how far* to open (a `targetZoom`
 *    from the nearby view check) is preferred over one that only knows "nothing here", whichever
 *    of them reported last
 */
export const resolveReferenceSignal = (
  chain: ReferenceStrategyId[],
  signals: ReferenceSignals
): ReferenceSignal | null => {
  let noneSignal: ReferenceSignal | null = null

  for (const strategyId of chain) {
    const signal = signals[strategyId]

    // Absent: this strategy has not reported yet and still might, so decide nothing.
    if (signal === undefined) {
      return null
    }

    // Explicit null: it reported that it has no opinion (unavailable, failed, or a value it does
    // not recognise). Skip it and keep asking the rest of the chain.
    if (signal === null) {
      continue
    }

    if (signal.reference !== 'none') {
      return signal
    }

    // The last 'none' reports, unless an earlier one knows how far to open and this one does not.
    if (!noneSignal || signal.targetZoom != null || noneSignal.targetZoom == null) {
      noneSignal = signal
    }
  }

  return noneSignal
}

/** Options refining how far {@link getReferenceAwareZoom} is allowed to open the map. */
export interface ReferenceZoomOptions {
  /**
   * DRRWEB-3399 — set when the resolved location carries a house number. A complete street
   * address orients the member on its own, so a 'none' opens to COMPLETE_ADDRESS_MIN_ZOOM (15)
   * instead of the city bands — in one move.
   */
  completeAddress?: boolean
  /**
   * DRRWEB-3399 — a data-driven widen target, computed by {@link resolveNearbyAgainstView} as the
   * smallest zoom that brings the nearest known landmark into the padded view. When given for a
   * 'none' reference it replaces the fixed bands: the map opens exactly as far as it needs to and
   * no further. Still clamped widen-only and never below CITY_LEVEL_ZOOM; the complete-address
   * floor (COMPLETE_ADDRESS_MIN_ZOOM) applies to it for the same reason it applies to the bands.
   * The clamp applies only to a 'none' reference — a 'target' reference (DRRWEB-3662, geocode) applies
   * this value verbatim.
   */
  targetZoom?: number
}

/**
 * Widen-only zoom ladder:
 *  - 'poi'    — a named landmark orients the member at any zoom, so the zoom is left alone
 *  - 'weak'   — a named road, park or river label orients them too: a member who can read the
 *               street they are on knows where they are, so the zoom is left alone. (An earlier cut
 *               stepped out on a lone road label; in practice that walked a house on a labelled
 *               residential street from 18 out to 16 for nothing.)
 *  - 'none'   — nothing rendered at all, so open out to NO_REFERENCE_FALLBACK_ZOOM (15), unless a
 *               `targetZoom` says exactly how far the nearest landmark is
 *  - 'target' — a geocode place-type signal (DRRWEB-3662) prescribes the exact zoom for the kind of
 *               place; applied verbatim, no widen-only clamp, no complete-address floor
 *
 * `completeAddress` keeps the 'none' fallback at COMPLETE_ADDRESS_MIN_ZOOM (15) and raises the
 * `targetZoom` floor to it: the member gave a house number, so the map never opens past the
 * neighbourhood even to bring a landmark into view. It is one move, not a walk — the map decides
 * once per landing. It has no effect on a 'target' reference.
 *
 * Never narrows for 'poi'/'weak'/'none': a zoom already wider than what this reference calls for is
 * returned as-is, and the widened result never goes below CITY_LEVEL_ZOOM, so this never opens as
 * wide as DEFAULT_MIN_ZOOM (4), which stays strictly a minZoom. A 'target' reference may zoom in or
 * out, per its table value.
 */
export const getReferenceAwareZoom = (
  currentZoom: number,
  reference: MapReference,
  options: ReferenceZoomOptions = {}
): number => {
  if (reference === 'poi' || reference === 'weak') {
    return currentZoom
  }

  // DRRWEB-3662 — a `geocode` signal prescribes an exact zoom for the kind of place. Applied as-is:
  // no widen-only clamp, no complete-address floor. The map's own maxZoom is the only bound.
  if (reference === 'target') {
    return options.targetZoom ?? currentZoom
  }

  if (options.targetZoom != null) {
    return clampWidenOnly(currentZoom, options.targetZoom, options.completeAddress)
  }

  return Math.min(
    currentZoom,
    options.completeAddress ? COMPLETE_ADDRESS_MIN_ZOOM : NO_REFERENCE_FALLBACK_ZOOM
  )
}

/** Widen-only, never below the complete-address floor (15) or the city level (9). */
const clampWidenOnly = (currentZoom: number, targetZoom: number, completeAddress?: boolean): number => {
  const floor = completeAddress ? COMPLETE_ADDRESS_MIN_ZOOM : CITY_LEVEL_ZOOM
  return Math.min(currentZoom, Math.max(targetZoom, floor))
}

/**
 * geocode strategy (DRRWEB-3662): the zoom Google Maps would open a place of this kind at, read from
 * PLACE_TYPE_TARGET_ZOOM. A place type outside the table (unknown, missing) is "no opinion" —
 * `null` — so the chain moves on and, alone, the map lands as control does. Decided pre-paint from
 * the geocode result already in hand: no request.
 */
export const classifyPlaceTypeZoom = (placeType: string | null | undefined): ReferenceSignal | null => {
  const targetZoom = placeType ? PLACE_TYPE_TARGET_ZOOM[placeType] : undefined
  if (targetZoom == null) {
    return null
  }
  return { reference: 'target', source: 'geocode', targetZoom, placeType }
}

/** AWS place types that name a real-but-weak reference: a street or a street address. */
export const WEAK_GEOCODE_PLACE_TYPES = new Set([
  'PointAddress',
  'InterpolatedAddress',
  'SecondaryAddress',
  'Street',
  'Intersection',
])

const isInside = (
  point: ViewPoint,
  box: { left: number; right: number; top: number; bottom: number }
): boolean =>
  point.x >= box.left && point.x <= box.right && point.y >= box.top && point.y <= box.bottom

/**
 * nearby detector, stage two (DRRWEB-3399): decides whether any SearchNearby candidate is actually
 * inside the visible, padded map at the current zoom. A result 117 m away is "near" by radius but
 * invisible at z18, where the canvas spans ~140 m — which is exactly the blank-square landing this
 * feature exists to prevent.
 *
 *  - any candidate inside the usable box -> the strongest one inside ('poi' beats 'weak')
 *  - none inside -> 'none', plus the zoom that would bring the nearest one in. The pin keeps its
 *    on-screen position when MapLibre re-zooms (setZoom preserves the center, and the center is
 *    already offset for the sheet), so the reach around the pin doubles per zoom level: the target
 *    is `currentZoom + floor(log2(min(availX / |dx|, availY / |dy|)))`, clamped widen-only and
 *    never below CITY_LEVEL_ZOOM.
 *
 * Pure and pixel-space so it is table-testable; the map component does the projecting.
 */
export const resolveNearbyAgainstView = ({
  pin,
  landmarks,
  viewport,
  currentZoom,
}: ViewProbe): ViewResolution => {
  const box = {
    left: viewport.padding.left,
    right: viewport.width - viewport.padding.right,
    top: viewport.padding.top,
    bottom: viewport.height - viewport.padding.bottom,
  }

  const inside = landmarks.filter((landmark) => isInside(landmark, box))
  if (inside.some((landmark) => landmark.strength === 'poi')) {
    return { reference: 'poi' }
  }
  if (inside.length) {
    return { reference: 'weak' }
  }

  // Nearest POI first, else nearest of anything — `landmarks` arrives nearest-first.
  const target = landmarks.find((landmark) => landmark.strength === 'poi') ?? landmarks[0]
  if (!target) {
    return { reference: 'none' }
  }

  const availX = Math.min(pin.x - box.left, box.right - pin.x)
  const availY = Math.min(pin.y - box.top, box.bottom - pin.y)
  const dx = Math.abs(target.x - pin.x)
  const dy = Math.abs(target.y - pin.y)

  // A degenerate box (pin outside the usable area) or a zero-distance candidate means there is
  // nothing meaningful to fit; report the measured absence and let the bands decide.
  if (availX <= 0 || availY <= 0 || (dx === 0 && dy === 0)) {
    return { reference: 'none' }
  }

  const ratios = [dx > 0 ? availX / dx : Infinity, dy > 0 ? availY / dy : Infinity]
  const delta = Math.floor(Math.log2(Math.min(...ratios)))
  const targetZoom = Math.min(currentZoom, Math.max(CITY_LEVEL_ZOOM, currentZoom + delta))

  return { reference: 'none', targetZoom }
}

/**
 * rendered detector (DRRWEB-3399): classifies which rendered layer-ids `queryRenderedFeatures`
 * returned near the pin. A POI hit outranks a weak hit; no hits in either bucket means the member
 * has nothing to orient against at the current zoom.
 */
export const classifyRenderedReference = (
  renderedLayerIds: string[],
  layers: ReferenceLayerIds
): MapReference => {
  const poiIds = new Set(layers.poi)
  const weakIds = new Set(layers.weak)

  if (renderedLayerIds.some((id) => poiIds.has(id))) {
    return 'poi'
  }

  if (renderedLayerIds.some((id) => weakIds.has(id))) {
    return 'weak'
  }

  return 'none'
}
